import qs from "query-string"
import amplitude from 'amplitude-js'
import { handleLoading, QueryStringBrand, SubDomain } from "../common/common"
import { createAction, setterAction } from "../common/redux"
import { setUserContext, captureException } from "../common/error"
import tracker from "../common/tracker"
import * as t from "./actionTypes"
import * as interviewActions from "../interview/actions"
import * as accountActions from "../account/actions"
import * as embedActions from "../embed/actions"
import Source from "../interview/Source"
import CommonSource from "../common/Source"
import { recycle } from "../redux/actions"
import * as generator from "../chatItemGenerator"
import { pushRoute } from "../common/route"
import { IS_PRODUCTION } from "../common/constants"
import { getBrand } from "../embed/actions"
import * as identifierActions from "../identifiers/actions"
import { checkIfTimedOut } from "../payment/actions"
import { setFeatures } from "../account/actions"
import { getPlanInformation } from "../dashboard/actions"
import { setCanUpdateInsurance } from "../integration/actions"

/*******************************************************************************
                              Action creators
 ******************************************************************************/
export const setFirstTime = setterAction(t.SET_FIRST_TIME)
export const setEmail = setterAction(t.SET_EMAIL)
export const setLoginStatus = setterAction(t.SET_LOGIN_STATUS)
export const setSignupStatus = setterAction(t.SET_SIGNUP_STATUS)
export const setPasswordResetStatus = setterAction(t.SET_PASSWORD_RESET_STATUS)
export const setResetActivated = setterAction(t.SET_RESET_ACTIVATED)
export const setLogoutStatus = setterAction(t.SET_LOGOUT_STATUS)
export const setBetaSubmitted = setterAction(t.SET_BETA_SUBMITTED)
export const setCanRegister = setterAction(t.SET_CAN_REGISTER)
export const setLogout = createAction(t.LOGOUT)
export const setIsAnonymous = setterAction(t.SET_IS_ANONYMOUS)
export const setSignupMessage = setterAction(t.SET_SIGNUP_MESSAGE)

export const loginSuccess = setterAction(t.LOGIN_SUCCESS)
export const loginError = setterAction(t.LOGIN_ERROR)
export const setPassSuccess = setterAction(t.SET_PASS_SUCCESS)
export const setPassError = setterAction(t.SET_PASS_ERROR)
export const requestResetSuccess = setterAction(t.REQUEST_RESET_SUCCESS)
export const requestResetError = setterAction(t.REQUEST_RESET_ERROR)
export const passResetSuccess = setterAction(t.PASS_RESET_SUCCESS)
export const passResetError = setterAction(t.PASS_RESET_ERROR)
export const signupSuccess = setterAction(t.SIGNUP_SUCCESS)
export const signupError = setterAction(t.SIGNUP_ERROR)

export const setViewedCookiePolicy = setterAction(t.SET_VIEWED_COOKIE_POLICY)

/*******************************************************************************
                                  Thunks
 ******************************************************************************/


export const loginSubmit = (password, redirect) => (dispatch, getState) => {
  let { email } = getState().auth
  return Source.loginSubmit(email, password)
    .then(data => {
      dispatch(loginSuccess(data))
      setUserContext({ email })
      if (data.location) {
        // Check to see if visit was transferred correctly
        dispatch(checkIfTimedOut())
        return pushRoute(data.location, true)
      }

      setTimeout(() => {
        if (redirect) {
          pushRoute(redirect[0] === "/" ? redirect : "/" + redirect, true)
        } else {
          pushRoute(data.redirect ? data.redirect : "/dashboard/")
        }
      }, 500)
    })
    .catch(error => {
      dispatch(loginError(error))
    })
}

export const setPassSubmit = (pass, passConfirm) => (dispatch, getState) => {
  if (pass !== passConfirm) {
    dispatch(setPassError({ msg: "Passwords must match" }))
    return
  }

  let { email } = getState().auth
  return handleLoading(dispatch, getState().loading, () =>
    Source.setPassSubmit(pass)
      .then(data => {
        setUserContext({ email })
        dispatch(setPassSuccess(data))
        tracker.newUser()
        if (!(typeof data === "string" && data.startsWith("<"))) {
          pushRoute("/dashboard/")
        }
      })
      .catch(error => {
        dispatch(setPassError(error))
      }), 
    "auth/setPassSubmit"
  )
}

export const setPassChatSubmit = pass => (dispatch, getState) => {
  return handleLoading(dispatch, getState().loading, () =>
    Source.setPassSubmit(pass).then(data => {
      dispatch(setPassSuccess(data))
      dispatch(
        interviewActions.addChatItems([
          generator.systemMessage(data.msg),
          generator.resultsDisclaimer(),
          generator.completion(),
        ])
      )
    }).catch(captureException),
    "auth/setPassChatSubmit"
  )
}

export const requestPasswordReset = () => (dispatch, getState) => {
  let { email } = getState().auth
  return handleLoading(dispatch, getState().loading, () =>
    Source.requestPasswordReset(email)
      .then(data => {
        dispatch(requestResetSuccess(data))
      })
      .catch(error => {
        dispatch(requestResetError(error))
      }),
    "auth/requestPasswordReset"
  )
}
export const submitPasswordReset = (pass, passConfirm) => (
  dispatch,
  getState
) => {
  if (pass !== passConfirm) {
    dispatch(setPassError({ msg: "Passwords must match" }))
    return
  }

  return handleLoading(dispatch, getState().loading, () =>
    Source.submitPasswordReset(pass)
      .then(data => {
        dispatch(passResetSuccess(data))
        if (window.location && window.location.href.indexOf("app=true") < 0)
          pushRoute("/activated/?resetpass=1&m=success")
      })
      .catch(error => {
        dispatch(passResetError(error))
      }),
    "auth/submitPasswordReset"
  )
}

export const logout = (redirect = true) => dispatch => {
  recycle()

  return Source.logout()
    .then(() => {
      dispatch(setLogout())
      if (redirect) {
        pushRoute("/dashboard/")
      }
    })
    .catch(error => dispatch(setLogoutStatus(error)))
}

export const signup = () => (dispatch, getState) => {
  let { email } = getState().auth
  return handleLoading(dispatch, getState().loading, () =>
    Source.signup(email)
      .then(data => {
        setUserContext({ email })
        dispatch(signupSuccess(data))
        tracker.emailSubmitted()
        if (data.location && window.location.href.indexOf("signup") > 0)
          pushRoute(data.location)
      })
      .catch(error => {
        dispatch(signupError(error))
        if (
          error.response.data &&
          error.response.data.location &&
          window.location.href.indexOf("signup") > 0
        ) {
          pushRoute(error.response.data.location)
        }
      }),
    "auth/signup"
  )
}

export const emailSubmit = () => (dispatch, getState) => {
  let { auth, completion, loading } = getState()
  if (loading) return
  let { email } = auth

  // Check if email is valid
  if (email.length && email.indexOf("@") < 0) {
    // TODO add an error field to new email inputs to show users why we are not
    // allowing submissions
    return
  }

  tracker.emailGiven()
  return Source.signup(email)
    .then(data => {
      setUserContext({ email })
      Source.recordAnonymous(email, completion.results.token)
      dispatch(signupSuccess(data))

      dispatch(setSignupMessage(data.msg))
    })
    .catch(error => {
      if (error.response.data.location.indexOf("/login/") >= 0) {
        return dispatch(setSignupMessage("PASSWORD"))
      } else if (error.response.data.location.indexOf("/signup/") >= 0) {
        return dispatch(setSignupMessage("SET_PASSWORD"))
      }
      dispatch(signupError(error))
    })
}

export const passwordSubmit = (password, onCompletion) => (
  dispatch,
  getState
) => {
  let { auth, completion, loading } = getState()
  if (loading) return
  let { email } = auth
  return Source.loginSubmit(email, password)
    .then(data => {
      setUserContext({ email })
      Source.recordAnonymous(email, completion.results.token)
      dispatch(loginSuccess(data))
      if (!onCompletion) {
        dispatch(
          interviewActions.addChatItems([
            generator.systemMessage(
              "A copy of your visit was saved to your profile."
            ),
            generator.resultsDisclaimer(),
            generator.completion(),
          ])
        )
      }
    })
    .catch(error => {
      dispatch(loginError(error))
    })
}

export const embeddedEmailSubmit = () => (dispatch, getState) => {
  let { auth, completion, loading, brand } = getState()
  if (loading) return
  let { email } = auth

  let items = []
  if (email.length) {
    setUserContext({ email })
    tracker.embeddedEmailGiven()
    Source.emailResults(completion.results.token, email, brand.name)
    items = [
      generator.systemMessage("A copy of your report has been sent to you."),
      generator.systemMessage(
        "You'll also receive a link where you can access " +
          "Buoy for all sorts of other health concerns."
      ),
      generator.systemMessage("Here are your final results."),
    ]
    return dispatch(signup(email))
  } else {
    tracker.embeddedEmailSkipped()
  }

  items.push(generator.resultsDisclaimer())
  items.push(generator.completion())
  dispatch(interviewActions.addChatItems(items))
}

export const getFeatures = () => (dispatch) => {
  Source.features().then(data => {
    window.features = data.features
    dispatch(setFeatures(data.features))
    let variants = {}
    Object.keys(data.features).forEach(key => {
      variants[key] = data.features[key]['variant']
    })
    amplitude.getInstance().setUserProperties(variants)
  }).catch(captureException)
}

export const getInitInfo = () => (dispatch, getState) => {
  const query = qs.parse(window.location.search)
  let domain

  // In prod, subdomains trump query strings for brands
  // But in other environments, query strings trump subdomains for testing purposes
  if (IS_PRODUCTION) {
    domain = SubDomain ? SubDomain : QueryStringBrand
  } else {
    domain = QueryStringBrand ? QueryStringBrand : SubDomain
  }

  let initialActions = []
  if (query.symptom) {
    initialActions.push(dispatch(embedActions.getCustom()))
  }

  initialActions.push(dispatch(getBrand(domain)))
  initialActions.push(dispatch(getFeatures(domain)))

  let configName
  if (query.configuration || domain) {
    configName = query.configuration ? query.configuration : domain
    initialActions.push(dispatch(interviewActions.processConfiguredExperience(configName)))
  } else {
    dispatch(setCanUpdateInsurance(true)) 
  }

  if (configName && configName === "ma_covid") {
    let locale = localStorage.getItem("locale") || navigator.language
    fetch("https://buoyhealth.com/_load")
      .then(r => {
        return r.json()
      })
      .then(d => {
        if (d.redirect) {
          if (locale.includes("es")) {
            window.location.replace("https://www.buoyhealth.com/diagnoses-a-z/lo-que-necesita-saber-coronavirus/")
          } else {
            window.location.replace("https://www.buoyhealth.com/diagnoses-a-z/coronavirus/")
          }
        }
      })
      .catch(captureException)
  }

  return Promise.all(initialActions).then(() => {
    return dispatch(accountActions.getProfiles()).then(() => {
      if (query.symptom) {
        dispatch(identifierActions.setPov("self"))
      } else {
        dispatch(interviewActions.chooseInterviewPath())
      }

      return CommonSource.viewedCookiePolicy().then(data => {
        if (data && !data.viewed_cookie_policy) {
          data.viewed_cookie_policy = false
        }
        dispatch(setViewedCookiePolicy(data.viewed_cookie_policy))

        const { configuredExperience, configuredPlanProvider } = getState().integration
        if (!configuredExperience || !configuredPlanProvider) {
          // Gets plan information to be used for getting recommendations.
          dispatch(getPlanInformation())
        }
      }).catch(captureException)
    }).catch(captureException)
  }).catch(captureException)
}

export const submitViewedCookiePolicy = viewed => dispatch => {
  CommonSource.updateViewedCookiePolicy(viewed).then(data => {
    dispatch(setViewedCookiePolicy(viewed))
  }).catch(captureException)
}



// WEBPACK FOOTER //
// ./assets/auth/actions.js